import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { GanttReponse } from "../models/GanttReponse";
import { ShiftInfo } from "../models/ShiftInfo";

const moment = getMomentTz();

export const getGanttData = async (
	params: {
		start: Date | undefined;
		end: Date | undefined;
		assetId: number | undefined;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Start", value: params.start?.toISOString() ?? null },
		{ name: "End", value: params.end?.toISOString() ?? null },
		{ name: "AssetId", value: params.assetId ?? null },
	];

	const resp = await apiService.callV2(
		"[M2].[GetGanttData]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const data = resp.data as GanttReponse;

		data.tables[0].rows = data.tables[0].rows.map((row) => {
			row.Start = moment.utc(row["Start"]).toDate();
			row.End = moment.utc(row["End"]).toDate();
			return row;
		});
		data.tables[1].rows = data.tables[1].rows.map((row) => {
			row.Start = moment.utc(row["Start"]).toDate();
			row.End = row["End"] ? moment.utc(row["End"]).toDate() : null;
			return row;
		});
		data.tables[2].rows = data.tables[2].rows.map((row) => {
			row.Timestamp = moment.utc(row["Timestamp"]).toDate();
			row.Start = moment.utc(row["Start"]).toDate();
			row.End = moment.utc(row["End"]).toDate();
			return row;
		});
		data.tables[3].rows = data.tables[3].rows.map((row) => {
			row.CreatedAt = moment.utc(row["CreatedAt"]).toDate();
			row.UpdatedAt = moment.utc(row["UpdatedAt"]).toDate();
			row.InstalledAt = moment.utc(row["InstalledAt"]).toDate();
			row.UninstalledAt = row["UninstalledAt"]
				? moment.utc(row["UninstalledAt"]).toDate()
				: null;
			row.DisassembledAt = moment.utc(row["DisassembledAt"]).toDate();
			row.SignedOffAt = moment.utc(row["SignedOffAt"]).toDate();
			return row;
		});
		data.tables[5].rows = data.tables[5].rows.map((row) => {
			row.RollingStart = row["RollingStart"]
				? moment.utc(row["RollingStart"]).toDate()
				: null;
			row.SectionSpansMultipleShifts =
				(row["SectionSpansMultipleShifts"] as unknown as number) === 1;
			return row;
		});
		data.tables[6].rows = data.tables[6].rows.map((row) => {
			row.Start = moment.utc(row["Start"]).toDate();
			row.End = moment.utc(row["End"]).toDate();
			return row;
		});
		data.tables[7].rows = data.tables[7].rows.map((row) => {
			row.Timestamp = moment.utc(row["Timestamp"]).toDate();
			return row;
		});
		data.tables[8].rows = data.tables[8].rows.map((row) => {
			row.Start = row["Start"] ? moment.utc(row["Start"]).toDate() : null;
			row.End = row["End"] ? moment.utc(row["End"]).toDate() : null;
			return row;
		});
		data.tables[9].rows = data.tables[9].rows.map((row) => {
			row.Start = row["Start"] ? moment.utc(row["Start"]).toDate() : null;
			row.End = row["End"] ? moment.utc(row["End"]).toDate() : null;
			row.CreatedAt = row["CreatedAt"]
				? moment.utc(row["CreatedAt"]).toDate()
				: null;
			return row;
		});

		return {
			errorMessage: null,
			tables: data.tables,
		} as GanttReponse;
	} else {
		throw new Error(resp.message || "Error fetching gantt data");
	}
};

export const getTrendingData = async (
	params: {
		start: Date | undefined;
		end: Date | undefined;
		assetId: number | undefined;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Start", value: params.start?.toISOString() ?? null },
		{ name: "End", value: params.end?.toISOString() ?? null },
		{ name: "AssetId", value: params.assetId ?? null },
	];

	const resp = await apiService.callV2(
		"[M2].[GetTrendingData]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", null) as ShiftInfo[];
	} else {
		throw new Error(resp.message || "Error fetching gantt data");
	}
};

export const upsertCarrierFromGantt = async ({
	carrierInstanceId,
	partId,
	rollsetId,
	sectionId,
	standId,
	installedAt,
	uninstalledAt,
	forceDeletePasses,
}: {
	carrierInstanceId: number;
	partId: number;
	rollsetId: number;
	sectionId: number;
	standId: number;
	installedAt: Date;
	uninstalledAt: Date;
	forceDeletePasses?: boolean;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "CarrierInstanceId", value: carrierInstanceId },
		{ name: "PartId", value: partId },
		{ name: "RollsetId", value: rollsetId },
		{ name: "SectionId", value: sectionId },
		{ name: "StandId", value: standId },
		{ name: "InstalledAt", value: installedAt.toISOString() },
		{ name: "UninstalledAt", value: uninstalledAt?.toISOString() },
	];

	if (forceDeletePasses) {
		parameters.push({ name: "ForceDeletePasses", value: true });
	}

	const resp = await apiService.callV2(
		"[M2].[UpsertCarrierInstanceFromGantt]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error installing carrier");
	}

	const message = get(
		resp,
		"data.outputParameters.@AUTOADJUSTMENTMESSAGES",
		null,
	);
	if (resp.ok && message) {
		return message;
	}
};

export const deleteCarrierFromGantt = async ({
	carrierInstanceId,
	operatorId,
}: {
	carrierInstanceId: number;
	operatorId: number;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "CarrierInstanceId", value: carrierInstanceId },
		{ name: "OperatorId", value: operatorId },
	];

	const resp = await apiService.callV2(
		"[M2].[DeleteCarrierInstance]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting carrier");
	}
};
